// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — the DOM screens: menu, help, about, and the two results panels. Pure HTML
// builders (no state), wired up by main.ts.

import { escapeHtml, fmtTime } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

function modeChips(modeId: string): string {
  return MODE_IDS.map(
    (id) =>
      `<button class="chip${id === modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === modeId}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
}

export function menuHtml(o: { modeId: string; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  const best = o.best > 0 ? `Best: ${fmtTime(o.best)}` : 'No time yet';
  return `
    <section class="screen menu">
      <div class="brand">
        <div class="logo" aria-hidden="true"></div>
        <h1>Teeter</h1>
        <p class="tagline">Tilt to roll the marble to the exit.</p>
      </div>
      <div class="modepick">
        <div class="chips" role="group" aria-label="Choose a mode">${modeChips(o.modeId)}</div>
        <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
        <p class="mode-best">${best}</p>
      </div>
      <div class="menu-actions">
        <button class="btn primary" id="playRandom">Play</button>
        <button class="btn" id="playDaily">Daily maze</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <h2>How to play</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <ul class="help-list">
      <li><strong>Tilt your phone</strong> to roll the marble. It obeys gravity — the further you tilt, the faster it rolls.</li>
      <li>Reach the <strong>glowing exit</strong>. Fastest time wins.</li>
      <li><strong>Sinkhole:</strong> dodge the dark pits — fall in and you roll back to the start.</li>
      <li><strong>Scatter:</strong> collect every gem, then the exit opens.</li>
      <li><strong>No tilt sensor?</strong> A joystick appears — drag to steer. On a computer, use the arrow keys.</li>
    </ul>
    <p class="help-foot">Tap <em>Recenter</em> any time to make "level" wherever you're holding the phone now.</p>
    <button class="btn primary sheet-close">Got it</button>`;
}

export function aboutHtml(): string {
  return `
    <h2>About Teeter</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <p>Roll a marble through a procedurally-generated maze by tilting your phone. Every maze is built
       from a seed, so a friend on the same link rolls the identical board — race them for the fastest time.</p>
    <p><strong>Privacy:</strong> Teeter reads your phone's tilt (motion) sensor to move the marble. Nothing is
       recorded, stored, or sent anywhere — it's just an angle turned into a direction, in your browser.</p>
    <p><strong>Multiplayer</strong> is peer-to-peer over WebRTC — there's no game server. Connecting to a friend
       uses a public signaling relay only to introduce the two browsers; no game data is stored on a server.</p>
    <p>No accounts, no cookies, no tracking. ${ATTRIB}</p>
    <button class="btn primary sheet-close">Close</button>`;
}

function medal(rank: number): string {
  return ['🥇', '🥈', '🥉'][rank] ?? `${rank + 1}.`;
}

function outcomeLabel(r: PeerResult, mode: Mode): string {
  if (r.left) return 'left';
  if (r.won) return fmtTime(r.ms);
  if (r.finished) {
    if (mode.gems > 0 && r.gemsLeft > 0) return `time out · ${mode.gems - r.gemsLeft}/${mode.gems} gems`;
    return 'time out';
  }
  return 'rolling…';
}

/** Solo results: the time, best, and a share line. */
export function soloResultsHtml(o: {
  won: boolean;
  ms: number;
  falls: number;
  gemsLeft: number;
  mode: Mode;
  best: number;
  daily: boolean;
  isNewBest: boolean;
}): string {
  const headline = o.won ? fmtTime(o.ms) : 'Out of time';
  const sub = o.won
    ? o.isNewBest
      ? 'New best time!'
      : o.best > 0
        ? `Best: ${fmtTime(o.best)}`
        : ''
    : o.mode.gems > 0
      ? `You collected ${o.mode.gems - o.gemsLeft}/${o.mode.gems} gems`
      : 'Try again';
  const falls = o.mode.holes > 0 && o.won ? `<p class="res-falls">${o.falls} fall${o.falls === 1 ? '' : 's'}</p>` : '';
  return `
    <section class="screen results">
      <p class="res-mode">${escapeHtml(o.mode.name)}${o.daily ? ' · Daily' : ''}</p>
      <h2 class="res-time ${o.won ? 'win' : 'dnf'}">${headline}</h2>
      <p class="res-sub">${escapeHtml(sub)}</p>
      ${falls}
    </section>`;
}

/** Multiplayer results: EVERY player's outcome (principle #9), plus the match tally. */
export function multiResultsHtml(
  ranked: PeerResult[],
  selfSeat: number | null,
  mode: Mode,
  tally: number[],
): string {
  const rows = ranked
    .map((r, i) => {
      const me = r.seat === selfSeat;
      return `<li class="res-row${me ? ' me' : ''}${r.left ? ' gone' : ''}">
        <span class="res-rank">${r.won ? medal(i) : '·'}</span>
        <span class="res-name">${escapeHtml(r.name)}${me ? ' <em>(you)</em>' : ''}</span>
        <span class="res-out">${escapeHtml(outcomeLabel(r, mode))}</span>
        <span class="res-wins">${tally[r.seat] ? `${'★'.repeat(Math.min(tally[r.seat], 5))}` : ''}</span>
      </li>`;
    })
    .join('');
  return `
    <p class="res-mode">${escapeHtml(mode.name)} · fastest to the exit wins</p>
    <ul class="res-list">${rows}</ul>`;
}
